var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useContext, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { Flex } from "../../Flex";
import { InputText } from "../../Inputs/InputText";
import { useUniqueId } from "../../../util/useUniqueId";
import { FormFieldWrapper } from "../FormFieldWrapper/FormFieldWrapper";
import { PrefixSuffixContainer } from "../internal/PrefixSuffixContainer";
import { GenericFormContext } from "../GenericFormContext";
import { FormFieldGroupContext } from "../FormFieldGroup/FormFieldGroup";
export const calculateBorderRadius = (hasPrefix, hasSuffix) => {
    if (hasPrefix && hasSuffix)
        return "0";
    if (hasPrefix)
        return "0 4px 4px 0";
    if (hasSuffix)
        return "4px 0 0 4px";
    return undefined;
};
/**
 *  <br /> <br />
 * ## Description
 * An input field within a form (along with optional label). <br>
 * The `FormInput` component is used to render a form input field with an optional label and helper text.
 * @example
 * <FormInput label="Password" name="password" inputType="password"/>
 */
export const FormInput = (_a) => {
    var { "aria-label": ariaLabel, autoComplete, autoFocus = false, disabled, fixedWidth, helperText = "", inputType, label, labelWidth, name = "", onKeyUp, registerProps, placeholder = "", prefix, readOnly = false, required = false, showLabel = true, suffix, value, variant = "primary", width } = _a, rest = __rest(_a, ["aria-label", "autoComplete", "autoFocus", "disabled", "fixedWidth", "helperText", "inputType", "label", "labelWidth", "name", "onKeyUp", "registerProps", "placeholder", "prefix", "readOnly", "required", "showLabel", "suffix", "value", "variant", "width"]);
    const id = useUniqueId();
    const descriptorId = useUniqueId();
    const { formState, getFieldState, getValues, register, setFocus } = useFormContext();
    const { invalid } = getFieldState(name, formState);
    const fieldValue = getValues(name);
    const { disabled: disabledFromFormFieldGroup } = useContext(FormFieldGroupContext);
    const disabledState = disabledFromFormFieldGroup || disabled;
    const { editIsAllowed } = useContext(GenericFormContext);
    const isReadOnly = readOnly || editIsAllowed === false;
    useEffect(() => {
        if (autoFocus) {
            setFocus(name);
        }
    }, [autoFocus, name, setFocus]);
    return (React.createElement(FormFieldWrapper, Object.assign({ "data-testid": `FormField-${name}`, descriptorId: descriptorId, fixedWidth: fixedWidth, helperText: helperText, label: showLabel ? label : undefined, labelHtmlFor: id, labelWidth: labelWidth, name: name, required: required || Boolean(registerProps === null || registerProps === void 0 ? void 0 : registerProps.required), width: width }, rest),
        React.createElement(Flex, { flexDirection: "row" },
            prefix && (React.createElement(PrefixSuffixContainer, { variant: "prefix", "data-testid": `FormInputPrefix-${name}`, disabled: disabled }, typeof prefix === "function" ? prefix({ name, value: fieldValue }) : prefix)),
            React.createElement(InputText, Object.assign({}, register(name, Object.assign(Object.assign(Object.assign({}, (disabledState ? { disabled: true } : {})), { 
                // We automate the validation rules
                value }), registerProps)), { "aria-label": ariaLabel, "aria-describedby": helperText && descriptorId, "aria-required": required, autoComplete: autoComplete, borderRadius: calculateBorderRadius(!!prefix, !!suffix), "data-testid": `FormInputText-${name}`, "data-trackid": `FormInputText-${name}`, disabled: disabledState, flex: "1", id: id, onKeyUp: onKeyUp, placeholder: placeholder, readOnly: isReadOnly, type: inputType || "text", variant: invalid ? "error" : variant, width: fixedWidth || width })),
            suffix &&
                // There are cases where the component requires extra behaviour around the container instead of inside the `PrefixSuffixContainer` (see `FormInputCopy`)
                (typeof suffix === "function" ? (suffix({ name, value: fieldValue })) : (React.createElement(PrefixSuffixContainer, { variant: "suffix", "data-testid": `FormInputSuffix-${name}`, disabled: disabled }, suffix))))));
};
